import React, { useState, useEffect, useCallback } from 'react';
import { componenteService } from "../../services/componenteService";
import { formatNumber } from "../../utils/numberFormat";
import { calculateGabinetePanels } from "../../utils/gabinetePanels";

// ✅ Mensaje emergente
const FeedbackMessage = ({ message, type, onClose }) => {
    if (!message) return null;
    setTimeout(onClose, 3000);

    return (
        <div
            style={{
                position: "fixed",
                top: 20,
                right: 20,
                background: type === "success" ? "#27ae60" : "#c0392b",
                color: "white",
                padding: "12px 18px",
                borderRadius: 8,
                zIndex: 999,
                fontWeight: "bold"
            }}
        >
            {message}
        </div>
    );
};

export default function GabineteForm() {
    const [gabinetes, setGabinetes] = useState([]);
    const [nuevoGabinete, setNuevoGabinete] = useState({
        nombre: "",
        frente: "",
        profundidad: "",
        altura: "",
        precioM2: "",
        precioDolar: "",
        incluirPiso: false,
        items: []
    });

    const [nuevoItem, setNuevoItem] = useState({ nombre: "", costo: "" });
    const [calc, setCalc] = useState(null);
    const [feedback, setFeedback] = useState({ message: "", type: "" });
    const [editingId, setEditingId] = useState(null);

    const cargar = useCallback(async () => {
        try {
            const data = await componenteService.gabinete.getAll();
            // Filtrar solo componentes únicos por nombre
            const unicos = data.filter(
                (item, idx, self) =>
                    self.findIndex((t) => t.nombre === item.nombre) === idx
            );
            setGabinetes(unicos || []);
        } catch {
            setFeedback({ message: "Error al cargar gabinetes.", type: "error" });
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const recalcular = () => {
        const frente = Number(nuevoGabinete.frente) || 0;
        const profundidad = Number(nuevoGabinete.profundidad) || 0;
        const altura = Number(nuevoGabinete.altura) || 0;

        if (!frente || !profundidad || !altura) {
            setCalc(null);
            return;
        }

        const panelPlan = calculateGabinetePanels({
            frente,
            profundidad,
            altura,
            precioM2: nuevoGabinete.precioM2,
            precioDolar: nuevoGabinete.precioDolar,
            incluirPiso: nuevoGabinete.incluirPiso,
        });
        const extras = nuevoGabinete.items.reduce((acc, i) => acc + (parseFloat(i.costo) || 0), 0);
        const costoFinal = panelPlan.costoMateriales + extras;

        setCalc({
            ...panelPlan,
            extras,
            costoFinal,
        });
    };

    useEffect(recalcular, [
        nuevoGabinete.frente,
        nuevoGabinete.profundidad,
        nuevoGabinete.altura,
        nuevoGabinete.precioM2,
        nuevoGabinete.precioDolar,
        nuevoGabinete.incluirPiso,
        nuevoGabinete.items
    ]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...nuevoGabinete,
                costoTotal: calc ? calc.costoFinal : 0,
                cantidadPaneles: calc ? calc.totalPaneles : 0,
                type: "Gabinete"
            };
            if (editingId) {
                await componenteService.gabinete.update(editingId, payload);
                setFeedback({ message: "Gabinete actualizado ✅", type: "success" });
            } else {
                await componenteService.gabinete.create(payload);
                setFeedback({ message: "Gabinete guardado ✅", type: "success" });
            }
            cancelarEdicion();
            setCalc(null);
            await cargar();
        } catch {
            setFeedback({ message: "Error al guardar los cambios ❌", type: "error" });
        }
    };

    const editarGabinete = (gabinete) => {
        setEditingId(gabinete.id);
        setNuevoGabinete({
            nombre: gabinete.nombre || "",
            frente: gabinete.frente ?? "",
            profundidad: gabinete.profundidad ?? "",
            altura: gabinete.altura ?? "",
            precioM2: gabinete.precioM2
                ?? (gabinete.precioM3 !== undefined && gabinete.precioM3 !== null
                    ? Number(gabinete.precioM3) * 0.1
                    : ""),
            precioDolar: gabinete.precioDolar ?? "",
            incluirPiso: gabinete.incluirPiso ?? false,
            items: Array.isArray(gabinete.items) ? gabinete.items : [],
        });
    };

    const cancelarEdicion = () => {
        setEditingId(null);
        setNuevoGabinete({
            nombre: "",
            frente: "",
            profundidad: "",
            altura: "",
            precioM2: "",
            precioDolar: "",
            incluirPiso: false,
            items: [],
        });
        setNuevoItem({ nombre: "", costo: "" });
    };

    const eliminarItem = (i) => {
        setNuevoGabinete((prev) => ({
            ...prev,
            items: prev.items.filter((_, idx) => idx !== i)
        }));
    };

    const editarPrecioItem = (i, nuevoPrecio) => {
        setNuevoGabinete((prev) => {
            const itemsActualizados = [...prev.items];
            itemsActualizados[i] = {
                ...itemsActualizados[i],
                costo: parseFloat(nuevoPrecio) || 0
            };
            return {
                ...prev,
                items: itemsActualizados
            };
        });
    };

    const eliminarGabinete = async (id, nombre) => {
        if (window.confirm(`¿Eliminar "${nombre}"?`)) {
            try {
                await componenteService.gabinete.delete(id);
                setFeedback({ message: "Gabinete eliminado ✅", type: "success" });
                cargar();
            } catch {
                setFeedback({ message: "Error al eliminar ❌", type: "error" });
            }
        }
    };

    return (
        <div className="components-wrapper">
            <FeedbackMessage
                message={feedback.message}
                type={feedback.type}
                onClose={() => setFeedback({ message: "", type: "" })}
            />

            <div className="components-container">

                <h2 className="title">Gestión de Componentes: Gabinete</h2>

                <form className="card" onSubmit={handleSubmit}>

                    <div className="grid">
                        <input placeholder="Nombre" name="nombre" value={nuevoGabinete.nombre} onChange={(e) => setNuevoGabinete({ ...nuevoGabinete, nombre: e.target.value })} />
                        <input placeholder="Frente (m)" type="number" step="0.01" name="frente" value={nuevoGabinete.frente} onChange={(e) => setNuevoGabinete({ ...nuevoGabinete, frente: e.target.value })} />
                        <input placeholder="Profundidad (m)" type="number" step="0.01" name="profundidad" value={nuevoGabinete.profundidad} onChange={(e) => setNuevoGabinete({ ...nuevoGabinete, profundidad: e.target.value })} />
                        <input placeholder="Altura (m)" type="number" step="0.01" name="altura" value={nuevoGabinete.altura} onChange={(e) => setNuevoGabinete({ ...nuevoGabinete, altura: e.target.value })} />
                        <input placeholder="Precio por m² (USD)" type="number" step="0.01" name="precioM2" value={nuevoGabinete.precioM2} onChange={(e) => setNuevoGabinete({ ...nuevoGabinete, precioM2: e.target.value })} />
                        <input placeholder="Precio USD → ARS" type="number" name="precioDolar" value={nuevoGabinete.precioDolar} onChange={(e) => setNuevoGabinete({ ...nuevoGabinete, precioDolar: e.target.value })} />
                    </div>

                    <label className="form-check">
                        <input
                            type="checkbox"
                            name="incluirPiso"
                            checked={nuevoGabinete.incluirPiso}
                            onChange={(e) => setNuevoGabinete({
                                ...nuevoGabinete,
                                incluirPiso: e.target.checked
                            })}
                        />
                        Incluir piso en el cálculo y el costo
                    </label>

                    {calc && (
                        <div className="results card">
                            <h3>📐 Paneles del muro</h3>
                            <p>
                                Frente (2 muros): {calc.frontWalls.fullPanels} placas enteras
                                {calc.frontWalls.cutPanels > 0
                                    ? ` + ${calc.frontWalls.cutPanels} ${calc.frontWalls.cutPanels === 1 ? "placa cortada" : "placas cortadas"} en ${calc.frontWalls.cutPieces} ${calc.frontWalls.cutPieces === 1 ? "pieza" : "piezas"} de ${formatNumber(calc.frontWalls.cutPieceWidth * 100, 1)} cm${calc.frontWalls.toleratedShortfall > 0 ? ` (se acepta cubrir ${formatNumber(calc.frontWalls.toleratedShortfall * 100, 1)} cm menos por pieza)` : ""}`
                                    : ""}
                            </p>
                            <p>
                                Profundidad (2 muros): {calc.depthWalls.fullPanels} placas enteras
                                {calc.depthWalls.cutPanels > 0
                                    ? ` + ${calc.depthWalls.cutPanels} ${calc.depthWalls.cutPanels === 1 ? "placa cortada" : "placas cortadas"} en ${calc.depthWalls.cutPieces} ${calc.depthWalls.cutPieces === 1 ? "pieza" : "piezas"} de ${formatNumber(calc.depthWalls.cutPieceWidth * 100, 1)} cm${calc.depthWalls.toleratedShortfall > 0 ? ` (se acepta cubrir ${formatNumber(calc.depthWalls.toleratedShortfall * 100, 1)} cm menos por pieza)` : ""}`
                                    : ""}
                            </p>
                            <p><strong>Total para muros: {calc.frontWalls.panels + calc.depthWalls.panels} placas</strong></p>
                            <p>Superficie de muros: {formatNumber(calc.areaParedes, 2)} m²</p>

                            <h3>🏠 Paneles del techo</h3>
                            <p>
                                {calc.roof.panels} placas orientadas sobre el lado corto ({formatNumber(Math.min(Number(nuevoGabinete.frente), Number(nuevoGabinete.profundidad)), 2)} m)
                                : {calc.roof.fullPanels} placas enteras de {formatNumber(calc.panelWidth, 2)} × {formatNumber(Math.min(Number(nuevoGabinete.frente), Number(nuevoGabinete.profundidad)), 2)} m
                                {calc.roof.cutPanels > 0
                                    ? ` + ${calc.roof.cutPanels} ${calc.roof.cutPanels === 1 ? "pieza cortada" : "piezas cortadas"} de ${formatNumber(calc.roof.cutPieceWidth * 100, 1)} cm × ${formatNumber(Math.min(Number(nuevoGabinete.frente), Number(nuevoGabinete.profundidad)), 2)} m`
                                    : ""}
                            </p>
                            <p>Superficie del techo: {formatNumber(calc.areaTecho, 2)} m² (incluido en el cálculo)</p>

                            <h3>🧱 Piso (opcional)</h3>
                            <p>
                                {calc.floor.panels} placas estimadas: {calc.floor.fullPanels} placas enteras de {formatNumber(calc.panelWidth, 2)} × {formatNumber(Math.min(Number(nuevoGabinete.frente), Number(nuevoGabinete.profundidad)), 2)} m
                                {calc.floor.cutPanels > 0
                                    ? ` + ${calc.floor.cutPanels} ${calc.floor.cutPanels === 1 ? "pieza cortada" : "piezas cortadas"} de ${formatNumber(calc.floor.cutPieceWidth * 100, 1)} cm × ${formatNumber(Math.min(Number(nuevoGabinete.frente), Number(nuevoGabinete.profundidad)), 2)} m`
                                    : ""}
                            </p>
                            <p>
                                {nuevoGabinete.incluirPiso
                                    ? `Superficie del piso: ${formatNumber(calc.areaPiso, 2)} m² (incluida en el total)`
                                    : `No incluido: al activar “Incluir piso” se agregarían ${calc.floor.panels} placas (${formatNumber(calc.areaPiso, 2)} m² de piso).`}
                            </p>

                            <h3>📦 Totales</h3>
                            <p><strong>Total paneles necesarios: {calc.totalPaneles}</strong></p>
                            <p>Superficie de paneles comprados: {formatNumber(calc.areaMateriales, 2)} m²</p>
                            <p>Material descartado: {formatNumber(calc.areaWaste, 2)} m²</p>
                            <p style={{ fontSize: '11px', color: '#666', fontStyle: 'italic', marginTop: '8px' }}>
                                💡 Cada panel mide 1.15 m × 2.50 m. Se incluyen 4 muros y techo; el piso es opcional.
                            </p>

                            <h3>✂️ Cortes</h3>
                            <p>Valor recortes: ${formatNumber(calc.costoSobrante)}</p>

                            <h3>💰 Costos</h3>
                            <p>Materiales: {formatNumber(calc.areaMateriales, 2)} m² × USD {formatNumber(nuevoGabinete.precioM2)} × ${formatNumber(nuevoGabinete.precioDolar)} = ${formatNumber(calc.costoMateriales)} ARS</p>
                            <p>Extras: ${formatNumber(calc.extras)}</p>

                            <h2 className="total">✅ Total: ${formatNumber(calc.costoFinal)} ARS</h2>
                        </div>
                    )}

                    {/* ITEMS */}
                    <h3 className="sub">Agregar Items adicionales</h3>
                    <div className="items-row">
                        <input placeholder="Nombre" value={nuevoItem.nombre} onChange={(e) => setNuevoItem({ ...nuevoItem, nombre: e.target.value })} />
                        <input placeholder="Costo" type="number" value={nuevoItem.costo} onChange={(e) => setNuevoItem({ ...nuevoItem, costo: e.target.value })} />
                        <button type="button" className="btn-add" onClick={() => {
                            if (!nuevoItem.nombre || !nuevoItem.costo) return;
                            setNuevoGabinete((prev) => ({
                                ...prev,
                                items: [...prev.items, { ...nuevoItem, costo: parseFloat(nuevoItem.costo) }]
                            }));
                            setNuevoItem({ nombre: "", costo: "" });
                        }}>+</button>
                    </div>

                    <table className="table">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Costo</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {nuevoGabinete.items.map((it, i) => (
                                <tr key={i}>
                                    <td>{it.nombre}</td>
                                    <td>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={it.costo}
                                            onChange={(e) => editarPrecioItem(i, e.target.value)}
                                            style={{
                                                width: "100%",
                                                padding: "4px",
                                                border: "1px solid #ddd",
                                                borderRadius: "4px"
                                            }}
                                        />
                                    </td>
                                    <td><button type="button" className="remove" onClick={() => eliminarItem(i)}>✕</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <button className="btn-save" type="submit">
                        {editingId ? "Guardar cambios del gabinete" : "Guardar Gabinete"}
                    </button>
                    {editingId && (
                        <button className="btn-cancel" type="button" onClick={cancelarEdicion}>
                            Cancelar edición
                        </button>
                    )}
                </form>

                <h2 className="title">Gabinetes Existentes ({gabinetes.length})</h2>

                <div className="grid-items">
                    {gabinetes.map((g) => (
                        <div className="card small" key={g.id} style={{ position: "relative" }}>
                            <h3>{g.nombre}</h3>
                            <p>{g.frente} × {g.profundidad} × {g.altura} m</p>
                            <p className="ok">AR$ {formatNumber(g.costoTotal)}</p>
                            <button
                                type="button"
                                className="btn-edit"
                                onClick={() => editarGabinete(g)}
                            >
                                ✏️ Editar
                            </button>
                            <button
                                onClick={() => eliminarGabinete(g.id, g.nombre)}
                                style={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    background: "#ef4444",
                                    color: "white",
                                    border: "none",
                                    borderRadius: 4,
                                    padding: "4px 8px",
                                    cursor: "pointer",
                                    fontSize: 12
                                }}
                            >
                                🗑️
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
